import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getElection } from '../services/api';
import './Pages.css';

const ElectionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [election, setElection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchElection = async () => {
      try {
        setLoading(true);
        const response = await getElection(id);
        setElection(response.data.election);
        setError(null);
      } catch (err) {
        console.error('Error fetching election:', err);
        setError('Failed to load election details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchElection();
  }, [id]);

  const getElectionTypeLabel = (type) => {
    switch (type) {
      case 1: return 'Single Choice';
      case 2: return 'Multi Choice';
      case 3: return 'Ranked Choice';
      default: return 'Unknown';
    }
  };

  const getElectionStatus = () => {
    if (!election) return {};
    
    const now = new Date();
    
    if (election.end_time && new Date(election.end_time) < now) {
      return { status: 'Ended', className: 'status-ended' };
    }
    
    if (election.start_time && new Date(election.start_time) > now) {
      return { status: 'Upcoming', className: 'status-upcoming' };
    }
    
    return { status: 'Active', className: 'status-active' };
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not specified';
    
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const { status, className } = getElectionStatus();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Loading election details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <h2>Error</h2>
        <p>{error}</p>
        <div className="error-actions">
          <button 
            className="btn btn-outline" 
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
          <Link to="/elections" className="btn btn-primary">
            Back to Elections
          </Link>
        </div>
      </div>
    );
  }

  if (!election) {
    return (
      <div className="not-found">
        <h2>Election Not Found</h2>
        <p>The election you're looking for doesn't exist or has been removed.</p>
        <Link to="/elections" className="btn btn-primary">
          View All Elections
        </Link>
      </div>
    );
  }

  return (
    <div className="election-detail-page">
      <div className="page-header">
        <div className="header-title">
          <button 
            className="btn-back" 
            onClick={() => navigate(-1)}
          >
            &larr; Back
          </button>
          <h1>{election.title}</h1>
        </div>
        <span className={`election-status ${className}`}>{status}</span>
      </div>

      <div className="election-detail-content">
        <div className="election-info card">
          <h2>Election Information</h2>
          <p className="election-description">{election.description || 'No description provided.'}</p>
          
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Type</span>
              <span className="info-value">{getElectionTypeLabel(election.election_type)}</span>
            </div>
            
            <div className="info-item">
              <span className="info-label">Max Candidates</span>
              <span className="info-value">{election.max_candidates}</span>
            </div>
            
            <div className="info-item">
              <span className="info-label">Max Selections</span>
              <span className="info-value">{election.max_selections}</span>
            </div>
            
            <div className="info-item">
              <span className="info-label">Start Time</span>
              <span className="info-value">{formatDateTime(election.start_time)}</span>
            </div>
            
            <div className="info-item">
              <span className="info-label">End Time</span>
              <span className="info-value">{formatDateTime(election.end_time)}</span>
            </div>
            
            <div className="info-item">
              <span className="info-label">Created</span>
              <span className="info-value">{formatDateTime(election.created_at)}</span>
            </div>
          </div>
        </div>

        <div className="candidates-section card">
          <h2>Candidates</h2>
          
          {!election.candidates || election.candidates.length === 0 ? (
            <p>No candidates added to this election yet.</p>
          ) : (
            <div className="candidates-list">
              {election.candidates.map((candidate) => (
                <div key={candidate.id} className="candidate-card">
                  <div className="candidate-name">{candidate.name}</div>
                  {candidate.description && (
                    <p className="candidate-description">{candidate.description}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="election-actions-container">
        {status === 'Active' && (
          <Link to={`/elections/${election.id}/vote`} className="btn btn-primary btn-lg">
            Vote Now
          </Link>
        )}
        
        {status === 'Ended' && (
          <Link to={`/elections/${election.id}/results`} className="btn btn-secondary btn-lg">
            View Results
          </Link>
        )}
        
        <Link to="/elections" className="btn btn-outline">
          Back to Elections
        </Link>
      </div>
    </div>
  );
};

export default ElectionDetailPage;